import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllSupervisor,
  selectAllSupervisor,
} from "../../../redux/users/usersSlice";
import { selectAdmin } from "../../../redux/admin/adminSlice";
import { Icon } from "@iconify-icon/react/dist/iconify.mjs";
import Loader from "../../custom-components/Loader/Loader";
import UserEditModal from "../../employee/UserEditModal";
import PopConfirm from "../../company/PopConfirm";
import { deleteUser } from "../../../utils/api/users";
import { toast } from "react-toastify";

function CompanySupervisor({ companyId, setRefresh }) {
  const dispatch = useDispatch();
  const supervisors = useSelector(selectAllSupervisor);
  const admin = useSelector(selectAdmin);

  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [scrollLoading, setScrollLoading] = useState(false);
  const [allSupervisors, setAllSupervisors] = useState([]);
  const [user, setUser] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [confirmPopup, setConfirmPopup] = useState(false);
  const tableBodyRef = useRef(null);

  useEffect(() => {
    const loadSupervisors = async () => {
      setLoading(true);
      try {
        const limit = 10;
        const result = await dispatch(
          getAllSupervisor({ companyId, page, limit })
        );
        const response = result.payload;
        if (!response.hasOwnProperty("status")) {
          const newSupervisors = response;
          if (newSupervisors.length < 10) setHasMore(false);

          setAllSupervisors((prevSupervisors) => [
            ...prevSupervisors,
            ...newSupervisors,
          ]);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadSupervisors();
  }, [dispatch, companyId, page]);

  useEffect(() => {
    if (allSupervisors.length > 0 && tableBodyRef.current) {
      tableBodyRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [allSupervisors]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target.documentElement;
    if (scrollHeight - scrollTop === clientHeight && hasMore && !loading) {
      setScrollLoading(true);
      setPage((prev) => prev + 1);
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasMore, loading]);

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      setPage((prev) => prev + 1);
    }
  };

  const handleDeleteUser = async (user) => {
    try {
      await deleteUser(user.id);
      toast.success("User Deleted");
      setPage(1);
      setHasMore(true);
      setAllSupervisors([]);
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <div className="w-[800px]">
      <div>
        <div className="flex gap-2">
          <h1 className="mb-4 text-2xl font-semibold">All Supervisors</h1>
        </div>
        {!loading ? (
          allSupervisors?.length > 0 ? (
            <>
              <div className="overflow-auto max-h-[400px]">
                {/* Scrollable table body */}
                <table className="min-w-full border border-collapse border-gray-300 shadow-lg table-auto">
                  <thead>
                    <tr className="bg-gray-200">
                      <th className="px-4 py-2 border border-gray-300">Name</th>
                      <th className="px-4 py-2 border border-gray-300">
                        Email
                      </th>
                      {admin.role === "administrator" && (
                        <th className="px-4 py-2 border border-gray-300">
                          Actions
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody ref={tableBodyRef}>
                    {allSupervisors.map((supervisor, index) => (
                      <tr
                        key={supervisor.id}
                        className={index % 2 === 0 ? "bg-gray-100" : "bg-white"}
                      >
                        <td className="px-4 py-2 border border-gray-300">
                          {supervisor.name}
                        </td>
                        <td className="px-4 py-2 border border-gray-300">
                          {supervisor.email}
                        </td>
                        {admin.role === "administrator" && (
                          <td className="px-4 py-2 border border-gray-300 text-center">
                            <div className="flex justify-center items-center gap-2">
                              <Icon
                                onClick={() => {
                                  setUser(supervisor);
                                  setOpenModal(true);
                                }}
                                className="text-lg text-blue-700 cursor-pointer"
                                icon="fluent:clipboard-text-edit-48-regular"
                              />
                              <Icon
                                onClick={() => {
                                  setUser(supervisor);
                                  setConfirmPopup(true);
                                }}
                                className="text-lg text-gray-700 cursor-pointer"
                                icon="mingcute:delete-2-fill"
                              />
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {hasMore && !loading && (
                <button
                  onClick={handleLoadMore}
                  className="w-full py-2 mt-4 text-white bg-blue-500 hover:bg-blue-600"
                >
                  Load More
                </button>
              )}

              {openModal && (
                <UserEditModal
                  setRefresh={setRefresh}
                  closeModal={() => setOpenModal(false)}
                  user={user}
                />
              )}

              {confirmPopup && (
                <PopConfirm
                  closePopup={() => setConfirmPopup(false)}
                  confirm={handleDeleteUser}
                  item={user}
                />
              )}
            </>
          ) : (
            <p>No supervisors found</p>
          )
        ) : (
          <div className="my-28">
            <Loader size={"w-24 h-24"} />
          </div>
        )}
      </div>
    </div>
  );
}

export default CompanySupervisor;
